import {
  Button,
  CheckBox,
  LoadIndicator,
  Lookup,
  Popup,
  TextArea,
  TextBox,
  ValidationSummary,
  Validator,
} from "devextreme-react";
import { useEffect, useRef, useState } from "react";
import { closePopup, reloadGrid } from "@/helper/DxHelper";
import { Dictionary, EnumItem, ValidationResultItem } from "@/types/types";
import { AxiosError } from "axios";
import { useDictUpdate } from "@/hooks/useDictUpdate";
import { ClickEvent } from "devextreme/ui/button";
import { RequiredRule } from "devextreme-react/validator";
import ServerResponse from "@/components/ServerResponse";
import { DictionaryLookupDS } from "@/dataSources/DictionaryLookupDS";
import { useDictionaryTypes } from "@/hooks/useDictionaryTypes";
import { axiosInstance } from "@/helper/axios-instance";
import { PopupRef } from "devextreme-react/cjs/popup";
import { DataGridRef } from "devextreme-react/cjs/data-grid";
import { useAppSelector } from "@/hooks/useAppSelector";
import { TextBoxRef } from "devextreme-react/cjs/text-box";
import { TextAreaRef } from "devextreme-react/cjs/text-area";
import { LookupRef } from "devextreme-react/cjs/lookup";
import { CheckBoxRef } from "devextreme-react/cjs/check-box";

type Props = {
  popupRef: React.RefObject<PopupRef>;
  datagridRef: React.RefObject<DataGridRef>;
};

const validationGroupName = "updateDictValGroup";

export default function Update({ datagridRef, popupRef }: Props) {
  const id = useAppSelector((state) => state.dictionary.editId);
  const [serverErrors, setServerErrors] = useState<ValidationResultItem[] | null>(null);
  const { mutate, data: response, isPending, isError, error, isSuccess } = useDictUpdate();
  const { data: dictionaryTypes } = useDictionaryTypes();
  const [userError, setUserError] = useState<string>("");
  const [anyChange, setAnyChange] = useState(false);
  const nameRef = useRef<TextBoxRef>(null);
  const fullnameRef = useRef<TextBoxRef>(null);
  const inactiveRef = useRef<CheckBoxRef>(null);
  const parentRef = useRef<LookupRef>(null);
  const valueRef = useRef<TextBoxRef>(null);
  const codeRef = useRef<TextBoxRef>(null);
  const commentRef = useRef<TextAreaRef>(null);
  const typeRef = useRef<LookupRef>(null);

  useEffect(() => {
    if (id) {
      hydrateForm();
    }
  }, [id]);

  useEffect(() => {
    if (isSuccess) {
      if (!response?.validation.items?.length) {
        closePopup(popupRef);
      } else {
        setServerErrors(response?.validation.items);
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    disableInputs(isPending);
  }, [isPending]);

  async function fetchAndSetData() {
    try {
      const response = await axiosInstance({
        method: "GET",
        url: `Dictionary/Get?id=${id}`,
      });
      const dataResponse = response.data?.data as Dictionary;

      nameRef.current?.instance().option("value", dataResponse.name);
      fullnameRef.current?.instance().option("value", dataResponse.fullname);
      inactiveRef.current?.instance().option("value", dataResponse.inactive);
      parentRef.current?.instance().option("value", dataResponse.parent);
      valueRef.current?.instance().option("value", dataResponse.value);
      codeRef.current?.instance().option("value", dataResponse.code);
      commentRef.current?.instance().option("value", dataResponse.comment);

      const idx = dictionaryTypes?.find((el: EnumItem) => dataResponse.type === el.key);
      typeRef.current?.instance().option("value", idx);

      popupRef?.current?.instance().option("title", `Edit ${dataResponse.name}`);
    } catch (error) {
      if (error instanceof AxiosError) setUserError(error.message);
    }

    setAnyChange(false);
  }

  function hydrateForm() {
    fetchAndSetData();
  }

  function disableInputs(state: boolean) {
    nameRef.current?.instance().option("disabled", state);
    fullnameRef.current?.instance().option("disabled", state);
    inactiveRef.current?.instance().option("disabled", state);
    parentRef.current?.instance().option("disabled", state);
    valueRef.current?.instance().option("disabled", state);
    codeRef.current?.instance().option("disabled", state);
    commentRef.current?.instance().option("disabled", state);
    typeRef.current?.instance().option("disabled", state);
  }

  function submit(e: ClickEvent) {
    setServerErrors(null);
    setAnyChange(false);

    if (!e.validationGroup.validate().isValid || !id) return;

    const requestData: Partial<Dictionary> = {
      id: id,
      name: nameRef.current?.instance().option("value"),
      fullname: fullnameRef.current?.instance().option("value"),
      inactive: inactiveRef.current?.instance().option("value"),
      parent: parentRef.current?.instance().option("value"),
      value: valueRef.current?.instance().option("value"),
      code: codeRef.current?.instance().option("value"),
      comment: commentRef.current?.instance().option("value"),
      type: typeRef.current?.instance().option("value").key,
    };

    mutate(requestData);
  }

  return (
    <Popup
      showCloseButton={true}
      ref={popupRef}
      onHiding={() => {
        setServerErrors(null);
        reloadGrid(datagridRef);
      }}
    >
      <>
        <div>
          {userError ? (
            <span>{userError}</span>
          ) : (
            <>
              <form className="mx-1 grid grid-cols-2 gap-8">
                <TextBox
                  labelMode="static"
                  ref={nameRef}
                  stylingMode="outlined"
                  valueChangeEvent="keyup"
                  label="Name"
                  onValueChanged={() => setAnyChange(true)}
                >
                  <Validator validationGroup={validationGroupName}>
                    <RequiredRule message={"Name is required"} />
                  </Validator>
                </TextBox>

                <TextBox
                  labelMode="static"
                  ref={fullnameRef}
                  stylingMode="outlined"
                  label="Full Name"
                  valueChangeEvent="keyup"
                  onValueChanged={() => setAnyChange(true)}
                />

                <TextBox
                  labelMode="static"
                  ref={valueRef}
                  label="Value"
                  stylingMode="outlined"
                  valueChangeEvent="keyup"
                  onValueChanged={() => setAnyChange(true)}
                />

                <TextBox
                  labelMode="static"
                  ref={codeRef}
                  label="Code"
                  stylingMode="outlined"
                  valueChangeEvent="keyup"
                  onValueChanged={() => setAnyChange(true)}
                />

                <Lookup
                  dataSource={DictionaryLookupDS(undefined)}
                  displayExpr={"name"}
                  stylingMode="outlined"
                  label="Parent"
                  labelMode="static"
                  ref={parentRef}
                  searchEnabled={true}
                  showClearButton={true}
                />

                <Lookup
                  searchEnabled={true}
                  label="Type"
                  items={dictionaryTypes ?? []}
                  displayExpr={"description"}
                  stylingMode="outlined"
                  labelMode="static"
                  ref={typeRef}
                />

                <TextArea
                  labelMode="static"
                  ref={commentRef}
                  stylingMode="outlined"
                  label="Comment"
                  valueChangeEvent="keyup"
                  onValueChanged={() => setAnyChange(true)}
                />

                <div className="col-start-2 mt-4">
                  <CheckBox ref={inactiveRef} text="Inactive" />
                </div>
              </form>
              <div className="absolute bottom-4 left-4">
                <div className="text-danger">
                  {isError && error instanceof AxiosError ? <span>{error.message}</span> : null}
                </div>
                <ValidationSummary validationGroup={validationGroupName} id="summary" />
                {serverErrors?.length ? <ServerResponse items={serverErrors} /> : null}
              </div>
            </>
          )}
        </div>
        <div className="absolute bottom-4 right-4">
          {anyChange && (
            <Button
              text="reset"
              onClick={hydrateForm}
              useSubmitBehavior={false}
              stylingMode="text"
              className="mr-4"
            />
          )}
          <Button
            validationGroup={validationGroupName}
            useSubmitBehavior={false}
            type="default"
            height={40}
            width={isPending ? 150 : 100}
            onClick={submit}
          >
            <LoadIndicator
              id="default-btn-loading"
              className="mr-3 inline-block align-middle"
              width={25}
              height={25}
              visible={isPending}
            />
            <span className="dx-button-text">{isPending ? "Updating" : "Update"}</span>
          </Button>
        </div>
      </>
    </Popup>
  );
}
